import { checkVAT, countries as vatCountries } from "jsvat-next";
import { ts } from "src/utils/translate";

const findVatCountries = (countryName: string) =>
    vatCountries.filter(c => c.name.toLowerCase() === countryName.toLowerCase());

// VAT numbers only apply in the EU, keep it in sync with PaymentUtil::isCountryCodeEU
const EU_COUNTRIES = [
    'Austria', 'Belgium', 'Bulgaria', 'Cyprus', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France',
    'Greece', 'Netherlands', 'Croatia', 'Ireland', 'Poland', 'Latvia', 'Lithuania', 'Luxembourg', 'Hungary',
    'Malta', 'Germany', 'Italy', 'Portugal', 'Romania', 'Spain', 'Sweden', 'Slovenia', 'Slovakia Republic',
    // outermost regions
    'French Guiana', 'Guadeloupe', 'Martinique', 'Mayotte', 'Reunion',
];

export const isVatCountry = (countryName: string): boolean => EU_COUNTRIES.includes(countryName);

export const isVatNumberValidForGivenCountry = (vatNumber: string, countryName: string): boolean => {
    if (!vatNumber) return true;

    const filteredCountries = findVatCountries(countryName);
    if (!filteredCountries.length) return true;

    return checkVAT(vatNumber, filteredCountries).isValid;
};

interface CompanyTaxFields {
    country: string;
    company: string;
    tax_number: string;
}

// switching to a country without VAT numbers drops the one entered for the previous country
export const clearVatNumberIfNotApplicable = <T extends CompanyTaxFields>(form: T, changedField: keyof T): T =>
    changedField === 'country' && !isVatCountry(form.country) ? { ...form, tax_number: '' } : form;

export const upperCaseVatNumberInput = (input: HTMLInputElement | HTMLTextAreaElement) => {
    const { selectionStart, selectionEnd } = input;
    input.value = input.value.toUpperCase();
    input.setSelectionRange(selectionStart, selectionEnd);
};

export const vatNumberExamples = () => ts("vat_number_format_examples",
    "e.g. HUXXXXXXXX, GBXXXXXXXXX, ROXXXXXXXXXX, DEXXXXXXXXX, NLXXXXXXXXXBXX, CZXXXXXXXX, SKXXXXXXXXXX, etc.");

export const validateCompany = (fields: CompanyTaxFields): string | undefined => {
    if (isVatCountry(fields.country) && fields.tax_number.trim() && !fields.company.trim()) {
        return ts("pf_company_required", "Company is required if you fill the VAT number field.");
    }
    return undefined;
};

export const validateVatNumber = (fields: CompanyTaxFields): string | undefined => {
    if (!isVatCountry(fields.country)) return undefined;

    const company = fields.company.trim();
    const taxNumber = fields.tax_number.trim();

    if (company && !taxNumber) {
        return ts("pf_taxnumber_required", "VAT number is required if you fill the Company field.");
    }
    if (!isVatNumberValidForGivenCountry(taxNumber, fields.country)) {
        return ts("jqval_msg_vatnumber_country", "Please enter a valid VAT number for the selected country");
    }
    return undefined;
};